import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';

/**
 * Wrapper around hCaptcha for the auth forms.
 * Dev/preview default: hCaptcha's public test sitekey — always passes.
 * Set REACT_APP_HCAPTCHA_SITEKEY in frontend/.env to use a real one.
 * A production build (yarn build) refuses to render a working widget on the
 * public test sitekey — it must be a real, configured sitekey.
 */
const TEST_SITEKEY = '10000000-ffff-ffff-ffff-000000000001';

const CaptchaWidget = forwardRef(({ onVerify, theme = 'dark' }, ref) => {
  const captchaRef = useRef(null);
  const configuredSitekey = process.env.REACT_APP_HCAPTCHA_SITEKEY || '';
  const isProductionBuild = process.env.NODE_ENV === 'production';
  const usingTestSitekey = !configuredSitekey || configuredSitekey === TEST_SITEKEY;

  useImperativeHandle(ref, () => ({
    reset: () => captchaRef.current?.resetCaptcha(),
    execute: () => captchaRef.current?.execute(),
  }));

  if (isProductionBuild && usingTestSitekey) {
    return (
      <div data-testid="captcha-widget-misconfigured" className="text-sm text-red-500 my-3 text-center">
        CAPTCHA is not configured for production. Set REACT_APP_HCAPTCHA_SITEKEY before deploying.
      </div>
    );
  }

  const sitekey = configuredSitekey || TEST_SITEKEY;

  return (
    <div data-testid="captcha-widget" className="flex justify-center my-3">
      <HCaptcha
        ref={captchaRef}
        sitekey={sitekey}
        theme={theme}
        onVerify={onVerify}
        onExpire={() => onVerify(null)}
        onError={() => onVerify(null)}
      />
    </div>
  );
});

CaptchaWidget.displayName = 'CaptchaWidget';
export default CaptchaWidget;
